/**
 * Enrichment Review Service
 *
 * Story 358: API client for enrichment review features
 * - Export missing fields (basic and with research)
 * - Search products
 * - Bulk correction upload
 *
 * Date: 2026-01-22
 */

import { authFetch } from '@/utils/auth-fetch';

const BASE_URL = '/api/enrichment-review';
const BULK_URL = '/api/bulk-correction';

// ============================================================================
// Types
// ============================================================================

export interface ExportFilters {
  categories?: string[];
  severity?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  priceTier?: 'premium' | 'mid' | 'entry' | null;
}

export interface ExportPreview {
  totalProducts: number;
  totalFields: number;
  estimatedResearchCost: string;
  estimatedResearchTimeMinutes: number;
  byPriceTier: {
    premium: number;
    mid: number;
    entry: number;
  };
  byCategory: Record<string, number>;
  topMissingFields: Array<{ name: string; count: number }>;
}

export interface ResearchJob {
  jobId: string;
  estimatedCost: number;
  estimatedTimeMinutes: number;
  totalProducts: number;
  totalFields?: number;
}

export interface ResearchJobStatus {
  jobId: string;
  status: 'pending' | 'researching' | 'generating_csv' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  totalProducts: number;
  processedProducts: number;
  valuesFound: number;
  notAvailable: number;
  failed: number;
  estimatedCost: number;
  actualCost: number;
  downloadUrl: string | null;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

export interface SearchResult {
  products: Array<{
    id: string;
    name: string;
    url: string;
    category: string;
    price: string;
    relevance: number;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface UploadPreviewError {
  row: number;
  field: string;
  error: string;
  value: string;
}

export interface UploadPreviewChange {
  productId: string;
  productName: string;
  fieldName: string;
  currentValue: string | null;
  newValue: string;
  type: 'add' | 'update';
}

export interface UploadPreview {
  previewId: string;
  totalRows: number;
  validCount: number;
  errorCount: number;
  errors: UploadPreviewError[];
  preview: UploadPreviewChange[];
}

export interface UploadResult {
  historyId: string;
  appliedCount: number;
  skippedCount: number;
  attributesUpdated: Record<string, number>;
}

export interface UploadHistoryItem {
  id: string;
  original_filename: string;
  file_size_bytes: number;
  total_rows: number;
  applied_count: number;
  skipped_count: number;
  error_count: number;
  changes_summary: {
    attributes_updated: Record<string, number>;
    products_affected: number;
  };
  status: string;
  uploaded_at: string;
  completed_at: string;
  uploaded_by_email: string;
  uploaded_by_name: string;
}

// ============================================================================
// Export Functions
// ============================================================================

/**
 * Get preview/summary of what would be exported
 */
export async function getExportPreview(siteId: string, filters: ExportFilters): Promise<ExportPreview> {
  const params = new URLSearchParams({ site_id: siteId });
  if (filters.categories?.length) params.append('categories', filters.categories.join(','));
  if (filters.severity) params.append('severity', filters.severity);
  if (filters.priceTier) params.append('price_tier', filters.priceTier);

  const response = await authFetch(`${BASE_URL}/export/preview?${params}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get export preview');
  }
  return response.json();
}

/**
 * Export missing fields as CSV (basic, no research)
 */
export async function exportMissingFields(siteId: string, filters: ExportFilters): Promise<Blob> {
  const params = new URLSearchParams({ site_id: siteId });
  if (filters.categories?.length) params.append('categories', filters.categories.join(','));
  if (filters.severity) params.append('severity', filters.severity);
  if (filters.priceTier) params.append('price_tier', filters.priceTier);

  const response = await authFetch(`${BASE_URL}/export/missing-fields?${params}`);
  if (!response.ok) {
    throw new Error('Export failed');
  }
  return response.blob();
}

/**
 * Start a research export job (background processing)
 */
export async function startResearchExport(siteId: string, filters: ExportFilters): Promise<ResearchJob> {
  const response = await authFetch(`${BASE_URL}/export/research`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, filters }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to start research export');
  }
  return response.json();
}

/**
 * Get research export job status
 */
export async function getResearchJobStatus(jobId: string): Promise<ResearchJobStatus> {
  const response = await authFetch(`${BASE_URL}/export/research/${jobId}/status`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get job status');
  }
  return response.json();
}

/**
 * Cancel a running research export job
 */
export async function cancelResearchJob(jobId: string): Promise<void> {
  const response = await authFetch(`${BASE_URL}/export/research/${jobId}/cancel`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to cancel job');
  }
}

/**
 * Download completed research export CSV
 */
export function getResearchDownloadUrl(jobId: string): string {
  return `${BASE_URL}/export/research/${jobId}/download`;
}

// ============================================================================
// Search Functions
// ============================================================================

/**
 * Search products by ID or name
 */
export async function searchProducts(
  siteId: string,
  query: string,
  options: { limit?: number; offset?: number } = {}
): Promise<SearchResult> {
  const params = new URLSearchParams({
    site_id: siteId,
    query,
    limit: String(options.limit || 50),
    offset: String(options.offset || 0),
  });

  const response = await authFetch(`${BASE_URL}/search?${params}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Search failed');
  }
  return response.json();
}

// ============================================================================
// Bulk Upload Functions
// ============================================================================

/**
 * Upload CSV for preview (validation only, no changes applied)
 */
export async function uploadCsvForPreview(siteId: string, file: File): Promise<UploadPreview> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('site_id', siteId);

  const response = await authFetch(`${BULK_URL}/upload/preview`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Upload failed');
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Upload failed');
  }

  return data;
}

/**
 * Apply corrections from a previewed upload
 */
export async function applyUploadCorrections(previewId: string): Promise<UploadResult> {
  const response = await authFetch(`${BULK_URL}/upload/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ previewId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Apply failed');
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Apply failed');
  }

  return data;
}

/**
 * Get upload history for a site
 */
export async function getUploadHistory(
  siteId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<{ history: UploadHistoryItem[]; total: number }> {
  const params = new URLSearchParams({
    site_id: siteId,
    limit: String(options.limit || 20),
    offset: String(options.offset || 0),
  });

  const response = await authFetch(`${BULK_URL}/upload/history?${params}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get history');
  }

  const data = await response.json();
  return data;
}

/**
 * Get download URL for an uploaded file
 */
export function getUploadDownloadUrl(historyId: string): string {
  return `${BULK_URL}/upload/history/${historyId}/download`;
}

/**
 * Get template download URL
 */
export function getTemplateDownloadUrl(): string {
  return `${BULK_URL}/upload/template`;
}
